/**
 * The garrison's purchases (03 §5.1 step 6): sentries, patrols, road outposts, the specials
 * of the higher threat ratings, emplacements. Each function places what was bought and
 * returns what it cost, or 0 when there was no room or not enough points.
 */

import type { Pcg32 } from '../sim/rng';
import { SPECIALS } from '../sim/tuning';
import type { EnemyKind } from '../sim/types';
import type { EmplacementKind } from './biomes';
import { type MapBuilder, NO_STAND } from './builder';
import { siteEmplacement, weightedKind } from './emplacements';
import { FLAG } from './grid';
import { addPatrol } from './patrols';
import { openGround, takePost } from './posts';
import { overlooksQuietApproach } from './quietSide';
import { COST, LIMITS } from './tuning';
import type { GenCtx } from './types';

/** Belt and goal soldiers of an elite garrison are elites; elsewhere they appear from threat 5 (02 §9.1). */
export function soldierKind(ctx: GenCtx, rng: Pcg32, bandIndex: number): EnemyKind {
  const kind = ctx.bands[bandIndex]?.kind;
  if (ctx.elite && (kind === 'belt' || kind === 'goal')) return 'elite';
  if (ctx.params.modifiers.includes('elite-garrison')) return 'elite';
  const threat = ctx.params.threat;
  return threat >= LIMITS.specialsFrom && rng.chance((threat - 4) * 0.1) ? 'elite' : 'rifleman';
}

const room = (ctx: GenCtx, n: number): boolean => ctx.map.spawns.length + n <= ctx.spawnCap;
const priceOf = (kinds: readonly EnemyKind[]): number => kinds.filter((k) => k === 'elite').length * COST.elite;

export function buySentry(ctx: GenCtx, rng: Pcg32, band: number, points: number, kind?: EnemyKind): number {
  if (!room(ctx, 1)) return 0;
  const who = kind ?? soldierKind(ctx, rng, band);
  const base = who === 'flamethrower' ? COST.flamethrower : who === 'rocketTrooper' ? COST.rocketTrooper : COST.sentry;
  const cost = base + priceOf([who]);
  if (cost > points) return 0;
  const isDrop = ctx.bands[band]?.kind === 'drop';
  // The drop band holds one sentry pair at most (03 §5.1 step 8): the second man stands by the first.
  const mate = isDrop ? ctx.map.spawns.find((s) => s.z > (ctx.bands[0]?.z0 ?? 0)) : undefined;
  for (let tries = 0; tries < 6; tries++) {
    const post = isDrop ? null : takePost(ctx, rng, band);
    const spot = post ?? openGround(ctx, rng, band, mate, mate ? 4 : 0);
    if (!spot) return 0;
    const facing = post ? post.facing + rng.range(-0.4, 0.4) : rng.range(-0.8, 0.8);
    // A man who never moves may not overlook the open end of the quiet way in: that is left to the patrols.
    if (overlooksQuietApproach(ctx, { kind: who, x: spot.x, z: spot.z, facing })) continue;
    ctx.map.spawn({ kind: who, x: spot.x, z: spot.z, facing });
    return cost;
  }
  return 0;
}

export function buyPatrol(ctx: GenCtx, rng: Pcg32, band: number, points: number, size: 2 | 3): number {
  if (!room(ctx, size)) return 0;
  const kinds: EnemyKind[] = [];
  for (let i = 0; i < size; i++) kinds.push(soldierKind(ctx, rng, band));
  let cost = (size === 2 ? COST.patrol2 : COST.patrol3) + priceOf(kinds);
  if (cost > points && !ctx.elite) {
    kinds.fill('rifleman');
    cost = size === 2 ? COST.patrol2 : COST.patrol3;
  }
  if (cost > points) return 0;
  const started = ctx.map.spawns.filter((s) => s.patrol !== undefined).length;
  return addPatrol(ctx, rng, band, kinds, started * 2) ? cost : 0;
}

/** Sandbags across the verge, a shelter, a crate, two men: a checkpoint on the road. */
export function buyOutpost(ctx: GenCtx, rng: Pcg32, band: number, points: number): number {
  if (!room(ctx, 2) || COST.outpost > points) return 0;
  const south = ctx.plan.lines[band - 1];
  const north = ctx.plan.lines[band];
  if (!south || !north) return 0;
  const side = south.roadX < ctx.sizeX / 2 ? 1 : -1;
  const x = Math.round((south.roadX + north.roadX) / 2 + side * 6);
  const z = Math.round((south.z + north.z) / 2 + rng.range(-5, 5));
  if (!ctx.map.place('sandbags', x, z + 2, { length: 4.4, margin: 1 })) return 0;
  ctx.map.place(ctx.kit.shelter, x + side * 6, z - 3, { margin: 1 });
  ctx.map.place('crate', x + side * 2, z - 2.5, { margin: 0.4 });
  for (const dx of [-1.2, 1.4]) {
    const spot = ctx.map.nearestOpen(x + dx, z + 0.4, 3, NO_STAND | FLAG.keep);
    if (!spot) continue;
    const man = { kind: soldierKind(ctx, rng, band), x: spot[0], z: spot[1], facing: rng.range(-0.3, 0.3) };
    if (!overlooksQuietApproach(ctx, man)) ctx.map.spawn(man);
  }
  return COST.outpost;
}

/**
 * An automated mini-bunker is a soldier to the sim, but it has a body the nav grid blocks
 * (hazards/terrain.ts): give it clear ground, off every road and beat, and mark it solid.
 */
export function placeMiniBunker(map: MapBuilder, x: number, z: number, reach = 3): boolean {
  const body = SPECIALS.bunkerBody + 0.5;
  const spot = map.nearestClear(x, z, body + 0.6, reach, NO_STAND | FLAG.keep | FLAG.road | FLAG.path);
  if (!spot) return false;
  map.spawn({ kind: 'miniBunker', x: spot[0], z: spot[1], facing: 0 });
  map.mask.circle(spot[0], spot[1], body, FLAG.solid);
  return true;
}

export function buyMiniBunker(ctx: GenCtx, rng: Pcg32, band: number, points: number): number {
  if (!room(ctx, 1) || COST.miniBunker > points) return 0;
  const cover = ctx.coverPoints.filter((c) => c.band === band);
  if (cover.length === 0) return 0;
  const at = rng.pick(cover);
  return placeMiniBunker(ctx.map, at.x + rng.range(-7, 7), at.z - rng.range(5, 10)) ? COST.miniBunker : 0;
}

const EMPLACEMENT_COST: Record<EmplacementKind, number> = {
  mgNest: COST.mgNest, mannedTurret: COST.mannedTurret, bunker: COST.bunker, turretBunker: COST.turretBunker,
};

/** The best emplacement the points allow, covering a crossing or gate in the band nobody covers yet. */
export function buyEmplacement(ctx: GenCtx, rng: Pcg32, band: number, points: number): number {
  if (!room(ctx, 1)) return 0;
  const kind = ctx.bands[band]?.kind;
  // Approach bands only get MG nests; the heavy works belong to the belt.
  const all: EmplacementKind[] = kind === 'approach' ? ['mgNest']
    : kind === 'goal' ? ['mgNest', 'bunker']
      : ['mgNest', 'bunker', 'mannedTurret', 'turretBunker'];
  const elite = soldierKind(ctx, rng, band) === 'elite';
  const extra = elite ? COST.elite : 0;
  const affordable = all.filter((k) => EMPLACEMENT_COST[k] + extra <= points);
  const cover = ctx.coverPoints.filter((c) => c.band === band && !c.used);
  if (affordable.length === 0 || cover.length === 0) return 0;
  const what = weightedKind(rng, ctx.kit.emplacements, affordable);
  const at = rng.pick(cover);
  if (!siteEmplacement(ctx, rng, what, at, elite)) return 0;
  at.used = true;
  return EMPLACEMENT_COST[what] + extra;
}
